import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, MailCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authErrorMessage } from '../lib/authErrors';
import AuthLayout, { AuthError, authInputClass, authLinkClass, authPrimaryButtonClass } from '../components/AuthLayout';

export const FORGOT_PASSWORD_CONFIRMATION =
  'Si el correo está registrado, recibirás un enlace para restablecer tu contraseña.';

// Supabase answers these the same way for known and unknown emails, so we can show them.
const isRetryableError = (message: string) =>
  message.startsWith('Demasiados intentos') || message.startsWith('No pudimos conectar');

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { resetPassword } = useAuth();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const { error } = await resetPassword(email.trim());
      const message = error ? authErrorMessage(error) : '';
      if (error) console.error('Password recovery error:', error);
      // Never reveal whether the account exists: only transient errors are surfaced.
      if (message && isRetryableError(message)) setError(message);
      else setSent(true);
    } catch (err) {
      console.error('Password recovery error:', err);
      setError(authErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Restablecer contraseña"
      description={sent ? undefined : 'Escribe tu correo y te enviaremos un enlace para crear una nueva contraseña.'}
      footer={
        <Link to="/login" className={authLinkClass}>
          Volver a iniciar sesión
        </Link>
      }
    >
      {sent ? (
        <div className="flex gap-3 rounded-lg bg-slate-50 p-4 text-sm text-slate-700 ring-1 ring-inset ring-slate-200" role="status">
          <MailCheck className="h-5 w-5 shrink-0 text-[color:var(--action)]" aria-hidden="true" />
          <p>{FORGOT_PASSWORD_CONFIRMATION}</p>
        </div>
      ) : (
        <form className="space-y-5" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700">
              Correo electrónico
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`mt-1 ${authInputClass}`}
              placeholder="correo@ejemplo.com"
            />
          </div>

          <AuthError message={error} />

          <button type="submit" disabled={isLoading} className={authPrimaryButtonClass}>
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {isLoading ? 'Enviando…' : 'Enviar enlace'}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
